import React from "react";
import {
  withStyles,
  CardContent,
  Card,
  CardActions,
  IconButton,
  CircularProgress,
  Popover,
} from "@material-ui/core";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@material-ui/icons";
import styles from "./styles";
import { getDataWithQueryString } from "../../../../../../api/componentAPIs";
import format from "date-fns/format";
import { sortBy } from "lodash";
import appConstants from "../../../../../../appConstants";
import Details from "./Details";

class StopSequenceHistory extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      expanded: false,
      isLoading: true,
      stopSequenceHistory: null,
      errorMessage: null,
    };
  }

  componentDidMount() {
    this._isComponentMounted = true;
    this.fetchSequenceChangeHistory();
  }

  componentWillUnmount() {
    this._isComponentMounted = false;
  }

  fetchSequenceChangeHistory = () => {
    const { terminalCode, routeId, stopId } = this.props;
    getDataWithQueryString(
      "StopChangeHistory",
      `terminalCode=${terminalCode}&routeId=${routeId}&stopId=${stopId}`
    ).then((response) => {
      if (response.status === 200) {
        if (!this._isComponentMounted) {
          return;
        }
        const { toCustomers, ...otherDetails } = response.data;
        const stopSequenceHistory = {
          ...otherDetails,
          toCustomers: this.getSortedStopSequence(toCustomers),
        };
        this.setState({ stopSequenceHistory, isLoading: false });
      } else {
        this.setState({
          errorMessage: "Failed to fetch stop sequence history",
          isLoading: false,
        });
      }
    });
  };

  getSortedStopSequence = (toCustomers) => {
    return sortBy(
      toCustomers,
      (sequenceData) =>
        sequenceData.changeTime && new Date(sequenceData.changeTime)
    ).reverse();
  };

  handleExpandClick = (event) => {
    event.persist();
    this.setState((prevState) => ({ expanded: !prevState.expanded }));
  };

  getTimeStringFromDateTime = (dateTimeString) => {
    return format(dateTimeString, appConstants.DATETYPES.TIME.format);
  };

  getNumberWithSign = (number) => {
    const newNumber = number && Number(number.toFixed(2)) || 0;
    if (!newNumber) {
      return newNumber ?? 0;
    }   
    return newNumber > 0 ? `-${newNumber}` : `+${Math.abs(newNumber)}`;
  };

  childKPIClass = (number) => {
    const { classes } = this.props;
    return {
      container:
        number >= 0 ? classes.positiveChildKpi : classes.negativeChildKpi,
      unitStyle: classes.unitStyle,
    };
  };
  parentKPIClass = (number) => {
    const { classes } = this.props;
    return {
      container:
        number >= 0 ? classes.positiveParentKpi : classes.negativeParentKpi,
      unitStyle: classes.unitStyle,
    };
  };
  render() {
    const { classes, stopSequenceAnchorEl } = this.props;
    const { expanded, stopSequenceHistory, isLoading, errorMessage } =
      this.state;
    const { name, changeTime, kpi } =
      stopSequenceHistory?.toCustomers?.[0] ?? {};
    return (
      <Popover
        id="stop-sequence"
        open={true}
        anchorEl={stopSequenceAnchorEl}
        onClose={this.props.closeModel}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        classes={{ paper: classes.popOver }}
        key={expanded ? "stop-sequence-expanded" : "stop-sequence"}
      >
        {Boolean(errorMessage) ? (
          <div className={classes.errorMessage}>{errorMessage}</div>
        ) : (
          <Card className={`${classes.cardContainer} customScroll`}>
            <CardContent classes={{ root: classes.rootContent }}>
              <span className={classes.customerName}>{name}</span>
              <br />
              <span className={classes.updatedBy}>
                Updated by Driver at
                <span className={classes.unitStyle}>
                  {this.getTimeStringFromDateTime(changeTime)}
                </span>
              </span>
              <div className={classes.kpiBoxContainer}>
                <Details
                  value={this.getNumberWithSign(kpi?.estimatedTravelTimeDifference)}
                  unit="min"
                  classes={this.parentKPIClass(
                    kpi?.estimatedTravelTimeDifference
                  )}
                />
                <Details
                  value={this.getNumberWithSign(kpi?.estimatedMilesDifference)}
                  unit="miles"
                  classes={this.parentKPIClass(kpi?.estimatedMilesDifference)}
                />
                <Details
                  value={kpi?.numServiceViolations}
                  unit="Missed Apt"
                  classes={{
                    container:
                      kpi?.numServiceViolations <= 0
                        ? classes.positiveParentKpi
                        : classes.negativeParentKpi,
                    unitStyle: classes.unitStyle,
                  }}
                />
              </div>
              <span className={classes.optymizerLabel}>
                Stop recommended by optymizer
              </span>
              <span className={classes.fromCustomerName}>
                {stopSequenceHistory?.fromCustomerName}
              </span>
            </CardContent>
            <CardActions className={classes.expandSection}>
              <span className={classes.expandLabel}>Sequence History</span>
              <IconButton
                onClick={this.handleExpandClick}
                aria-label="show more"
                className={classes.expandButton}
              >
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </CardActions>
            {expanded && (
              <CardContent
                className={classes.historyContainer}
                classes={{ root: classes.childContent }}
              >
                {stopSequenceHistory?.toCustomers?.map((customer, index) => (
                  <React.Fragment key={index}>
                    <span className={classes.updatedBy}>
                      {this.getTimeStringFromDateTime(customer.changeTime)}
                    </span>
                    <span className={classes.updatedBy}>{customer.name}</span>
                    <div className={classes.historyKpiContainer}>
                      <Details
                        value={this.getNumberWithSign(
                          customer.kpi.estimatedTravelTimeDifference
                        )}
                        unit="min"
                        classes={this.childKPIClass(
                          customer.kpi.estimatedTravelTimeDifference
                        )}
                      />
                      <Details
                        value={this.getNumberWithSign(
                          customer.kpi.estimatedMilesDifference
                        )}
                        unit="miles"
                        classes={this.childKPIClass(
                          customer.kpi.estimatedMilesDifference
                        )}
                      />
                      <Details
                        value={customer.kpi.numServiceViolations}
                        unit="Missed Apt"
                        classes={{
                          container:
                            customer.kpi.numServiceViolations <= 0
                              ? classes.positiveParentKpi
                              : classes.negativeParentKpi,
                          unitStyle: classes.unitStyle,
                        }}
                      />
                    </div>
                  </React.Fragment>
                ))}
              </CardContent>
            )}

            {isLoading && (
              <div className={classes.loader}>
                <CircularProgress size={26} />
              </div>
            )}
          </Card>
        )}
      </Popover>
    );
  }
}

export default withStyles(styles)(StopSequenceHistory);
